/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SidebarNavDropdown';
import { toOptions } from 'componentregistry/utils/ManagerUtils';

const SidebarNavDropdownManager: ComponentManagerFn<'SidebarNavDropdown'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SidebarNavDropdown'>(
      platform,
      'SidebarNavDropdown',
    ),
    type: 'SidebarNavDropdown',
    name: 'Sidebar Nav Dropdown',
    isChildAllowed: (page, item, childType) =>
      (['SidebarNavLink', 'SidebarNavDropdown'] as ItemType[]).includes(
        childType,
      ),
    defaultValue,
    props: {
      name: {
        label: 'Name',
        type: 'string',
      },
      fontSize: {
        label: 'Font Size',
        type: 'string',
        category: 'Look & Feel',
      },
      fontWeight: {
        label: 'Font Weigth',
        type: 'number',
        min: 100,
        max: 900,
        step: 100,
        category: 'Look & Feel',
      },
      fontStyle: {
        label: 'Font Style',
        type: 'select',
        options: toOptions(['italic', 'normal', 'oblique']),
        category: 'Look & Feel',
      },
      fontFamily: {
        label: 'Font Family',
        type: 'string',
        category: 'Look & Feel',
      },
      fontColor: {
        label: 'Font Color',
        type: 'ColorExpression',
        placeholder: 'e.g. #a2a0a2 or blue or primary',
        category: 'Look & Feel',
      },
      icon: {
        label: 'Nav Menu Icon',
        type: 'iconselect',
      },
      expandIcon: {
        label: 'Expand Icon',
        type: 'iconselect',
      },
      collapseIcon: {
        label: 'Collapse Icon',
        type: 'iconselect',
      },
      expanded: {
        label: 'Expanded',
        type: 'boolean',
      },
    },
    getDisplayText: (item) => '',
    category: 'Sidebar',
    canHaveChildren: () => true,
    isPropertyVisible(prop, props, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        appUid &&
        platform &&
        itemId &&
        pageId &&
        prop === 'expanded'
      ) {
        const parentId = platform.findParentOfType(
          appUid,
          pageId,
          itemId,
          'SidebarNavDropdown',
        );
        return !parentId;
      } else {
        return true;
      }
    },
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Sidebar Nav Dropdown is a hierarchical navigation component designed for Sidebar Panels. It creates a collapsible/expandable menu section that allows users to organize navigation links into sub-menus. This is essential for keeping complex navigation structures clean and accessible by hiding secondary links until needed.
Use this component to build nested menus, such as a "Reports" section that expands to show "Sales Reports," "Inventory Logs," and "Audit Trails."

**1. Structure & Nesting**<br/>
This component acts as both a container and a navigation item.
* **Allowed Children:** Supports SidebarNavLink for actual clickable links and SidebarNavDropdown for nesting dropdowns inside other dropdowns to create multi-level menus (e.g., Settings > User Management > Roles).

**2. Configuration**<br/>
* **Name:** The text label displayed for the dropdown header (e.g., "Administration").
* **Expanded:** A boolean property that determines if the dropdown loads in an open state by default. Note that this property is generally visible only on the root dropdown.

**3. Visual Customization**<br/>
You have extensive control over the typography and iconography to match the menu style to your application branding.
* **Typography (Look & Feel):** Customize Font Size, Font Family, Font Weight (100-900), Font Style (normal, italic, oblique), and Font Color (supports ColorExpression).
* **Nav Menu Icon:** The primary icon displayed next to the dropdown name.
* **Expand Icon:** The icon shown when the menu is closed (typically a generic "chevron right" or "plus").
* **Collapse Icon:** The icon shown when the menu is open (typically a "chevron down" or "minus").

**4. Best Use Cases**<br/>
* **Multi-Level Navigation:** Organizing a large app with hundreds of pages into logical categories.
* **Space Saving:** Keeping the sidebar clean by collapsing rarely used sections like "Archives" or "Legacy Tools."
* **Contextual Groups:** Grouping links by department (e.g., "Finance," "HR," "IT").`,
  };
};

export default SidebarNavDropdownManager;
